import { useState } from "react";
import { CalendarDays, ExternalLink, Eye, EyeOff, Trash2 } from "lucide-react";
import {
  AddRow,
  Button,
  Card,
  DragHandle,
  EmptyState,
  Icon,
  PLATFORMS,
  PLATFORM_GROUPS,
  Picker,
  PlatformIcon,
  Skeleton,
  UrlBar,
  useDropTarget,
} from "@cntrl/ui";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { PUBLIC_WEB_URL } from "../../config/env.js";
import {
  useLinkInBio,
  useUpdateLinkInBio,
  type LinkInBioRead,
  type TourDatesLink,
} from "../../app/hooks/useLinkInBio.js";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import { useUnsavedChanges, saveButtonState } from "./useUnsavedChanges.js";
import { MerchShelfSection } from "./MerchShelfSection.js";
import styles from "./LinksCard.module.scss";

const LINK_DRAG_KIND = "link-in-bio";
const MAX_ROWS = 50;

/**
 * Assets → Link in Bio (shot-30). ONE draggable list holding the band's manual rows AND the AUTO rows derived
 * from connected socials/streaming, plus a tour-dates row and the short-bio card. (The Stats mode and its
 * Links|Stats toggle were removed 2026-08-16 — deprecated; the server endpoint still exists.)
 * The whole surface is write-gated (assets:write) like Brand. The short bio reuses the shared bios endpoint
 * (core.bands.short_bio).
 *
 * The two row KINDS share the list and the drag order but not their editability (ART-51). A manual row is the
 * band's: label, URL and platform are all editable and the trash removes it. An auto row is the CONNECTION's:
 * its text is read-only — renaming it here would be renaming something the next sync would contradict — and
 * instead of a trash it carries a visibility toggle, because you cannot delete a row that is going to be
 * re-derived on the next render. Hidden rows stay in this list, dimmed, or there would be nothing left to
 * click to bring them back.
 *
 * Tour Dates is deliberately NOT in the list: it keeps its own block below, unordered and unhideable.
 */
/**
 * One manual link row. The grip is the drag source AND keyboard control; the row is the drop target.
 * `draggable` deliberately stays off the row — it holds two text inputs.
 */
function LinkRow({
  index, count, row, onMove, onPatch, onRemove,
}: {
  index: number;
  count: number;
  row: Draft;
  onMove: (from: number, to: number) => void;
  onPatch: (changes: Partial<Draft>) => void;
  onRemove: () => void;
}) {
  const drop = useDropTarget({ index, kind: LINK_DRAG_KIND, onMove });
  const name = row.label.trim() || `link ${index + 1}`;
  return (
    <li className={styles.row} {...drop}>
      <DragHandle label={name} index={index} count={count} kind={LINK_DRAG_KIND} onMove={onMove} />
      {/* The mark mirrors the picker beside it — it is a preview of the choice, never a second control. */}
      <PlatformIcon platform={row.icon} size={20} className={styles.rowIcon} />
      <div className={styles.rowInputs}>
        <Picker
          bare
          label={`Link ${index + 1} platform`}
          value={row.icon ?? "custom"}
          triggerClassName={styles.platformSelect}
          options={PLATFORM_GROUPS.flatMap((group) =>
            PLATFORMS.filter((pl) => pl.group === group).map((pl) => ({
              value: pl.key,
              label: pl.label,
              group,
            })),
          )}
          onChange={(icon) => onPatch({ icon })}
        />
        <input className={styles.labelInput} value={row.label} placeholder="Label"
          aria-label={`Link ${index + 1} label`} onChange={(e) => onPatch({ label: e.target.value })} />
        <input className={styles.urlInput} value={row.url} placeholder="Link URL" inputMode="url"
          aria-label={`Link ${index + 1} URL`} onChange={(e) => onPatch({ url: e.target.value })} />
      </div>
      <a className={styles.openBtn} href={isHttp(row.url) ? row.url : undefined}
        aria-disabled={!isHttp(row.url)} target="_blank" rel="noopener noreferrer"
        aria-label={`Open ${name}`}>
        <Icon icon={ExternalLink} size={16} />
      </a>
      <button type="button" className={styles.ctrlDanger} aria-label={`Remove ${name}`} onClick={onRemove}>
        <Icon icon={Trash2} size={16} />
      </button>
    </li>
  );
}

/**
 * One AUTO row, in the SAME list and with the same drag affordance as a manual row — that is the feature. What
 * differs is everything downstream of ownership: the label and URL are the connection's, so they render as text
 * rather than inputs, and the trailing control hides instead of deletes. Deleting is not on offer because the
 * row is derived: it would be back on the next render, which is exactly the "control that silently does
 * nothing" this codebase keeps finding.
 *
 * A hidden row stays visible HERE (dimmed) and disappears only from the public page.
 */
function AutoRow({
  index, count, row, onMove, onToggleHidden,
}: {
  index: number;
  count: number;
  row: Draft;
  onMove: (from: number, to: number) => void;
  onToggleHidden: () => void;
}) {
  const drop = useDropTarget({ index, kind: LINK_DRAG_KIND, onMove });
  const name = row.label.trim() || row.source || `link ${index + 1}`;
  return (
    <li
      className={[styles.row, styles.rowAuto, row.hidden ? styles.rowHidden : ""].filter(Boolean).join(" ")}
      {...drop}
    >
      <DragHandle label={name} index={index} count={count} kind={LINK_DRAG_KIND} onMove={onMove} />
      <PlatformIcon platform={row.icon} size={20} className={styles.rowIcon} />
      <div className={styles.rowStatic}>
        <span className={styles.rowStaticLabel}>{name}</span>
        <span className={styles.rowStaticNote} title={row.note}>
          {row.hidden ? `Hidden from your page · ${row.note}` : row.note}
        </span>
      </div>
      <a className={styles.openBtn} href={isHttp(row.url) ? row.url : undefined}
        aria-disabled={!isHttp(row.url)} target="_blank" rel="noopener noreferrer"
        aria-label={`Open ${name}`}>
        <Icon icon={ExternalLink} size={16} />
      </a>
      {/* aria-pressed, not a checkbox: this is a toggle button whose pressed state IS "hidden". */}
      <button type="button" className={styles.ctrlToggle} aria-pressed={row.hidden}
        aria-label={row.hidden ? `Show ${name} on your page` : `Hide ${name} from your page`}
        onClick={onToggleHidden}>
        <Icon icon={row.hidden ? EyeOff : Eye} size={16} />
      </button>
    </li>
  );
}

/**
 * The tour row's icon: the band's LIVE shot (Brand photo slot 3), falling back to a glyph when that slot
 * is empty.
 *
 * It resolves through `useMediaObjectUrl` like every other band photo in this app, NOT through the DTO's
 * `iconUrl` alone — that URL comes from `TryPublicUrl`, which is null wherever no public media host is
 * configured (all of local dev), so a url-only render drew the glyph even when the photo existed. The hook
 * takes the URL as an optimisation and otherwise fetches the authenticated `/media/{id}/raw`.
 */
function TourIcon({ tour }: { tour: TourDatesLink }) {
  const resolved = useMediaObjectUrl(tour.iconMediaId ?? null, tour.iconUrl ?? null, { variant: "thumb" });
  return resolved.url
    ? <img className={styles.tourIcon} src={resolved.url} alt="" />
    : <Icon icon={CalendarDays} size={20} className={styles.autoIcon} />;
}

function LoadingRows() {
  return (
    <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
      <Skeleton variant="line" width="60%" />
      <Skeleton variant="line" width="80%" />
      <Skeleton variant="line" width="45%" />
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <EmptyState
      title="Couldn't load"
      description={error instanceof ApiError ? error.message : "Please try again."}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}

// ---- Links editor ---------------------------------------------------------------------------------------------

export function LinksCard() {
  const q = useLinkInBio();
  // Error/loading branch ONLY on the initial load (no data yet). Once data exists, keep LinksEditor mounted even
  // through a FAILED background refetch (isError=true while last-good data is retained) — else the imperative
  // refetchAllScopes() a WS reconnect fires (which bypasses EDITOR_READ) would unmount the editor and destroy the
  // unsaved manual-row draft (adversarial-review MED). Draft safety is the whole point of decoupling from the query.
  if (!q.data) {
    return (
      <Card>
        {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <LoadingRows />}
      </Card>
    );
  }
  // Deliberately NOT keyed on dataUpdatedAt: the manual-row draft lives in LinksEditor's local state (initialised
  // once, reset only on an explicit save), so a (successful) background refetch — e.g. the connection-changed
  // invalidation that refreshes the AUTO rows live — updates the read-only auto/tour/URL props WITHOUT clobbering
  // unsaved manual edits (the HIGH draft-clobber finding, resolved by decoupling the draft from the query).
  return <LinksEditor initial={q.data} />;
}

/**
 * One entry in the unified draft list. `source` is the KIND discriminator, exactly as it is on the wire:
 * null ⇒ a manual row the band owns; set ⇒ an auto row whose `label`/`url`/`note` are display-only copies of
 * what the server derived and are never sent back.
 */
type Draft = {
  linkId: string | null;
  source: string | null;
  label: string;
  url: string;
  icon: string | null;
  note: string;
  hidden: boolean;
};

const isManual = (d: Draft) => d.source === null;

/**
 * Merge the server's two lists into the ONE ordered list the editor edits. Both carry `position` in a single
 * sequence, so this is a sort rather than a concat — which is the whole point: an auto row can sit between two
 * manual ones. Ties keep manual first, matching the public query, and can only arise from the
 * never-reordered default.
 */
function toDraft(read: LinkInBioRead): Draft[] {
  const manual = (read.rows ?? []).map((r) => ({
    linkId: r.linkId ?? null,
    source: null,
    label: r.label ?? "",
    url: r.url ?? "",
    icon: r.icon ?? null,
    note: "",
    hidden: false,
    position: r.position ?? 0,
    kind: 0,
  }));
  const auto = (read.autoRows ?? []).map((a) => ({
    linkId: null,
    source: a.source ?? null,
    label: a.label ?? "",
    url: a.url ?? "",
    icon: a.icon ?? null,
    note: a.note ?? "",
    hidden: a.hidden ?? false,
    position: a.position ?? 0,
    kind: 1,
  }));
  return [...manual, ...auto]
    .sort((x, y) => x.position - y.position || x.kind - y.kind)
    .map(({ position: _position, kind: _kind, ...d }) => d);
}

function LinksEditor({ initial }: { initial: LinkInBioRead }) {
  const save = useUpdateLinkInBio();
  const [rows, setRows] = useState<Draft[]>(() => toDraft(initial));
  const { unsaved, markSaved } = useUnsavedChanges(rows);

  const publicUrl = absolutePublicUrl(initial.publicPath);
  const tour = initial.tourDates;

  // Only MANUAL rows can be invalid or count toward the cap: an auto row's text is the connection's (never
  // blank, never the band's to break), and a connection must not spend the band's own 50-link budget.
  const invalid = rows.some((r) => isManual(r) && (r.label.trim() === "" || r.url.trim() === ""));
  const tooMany = rows.filter(isManual).length > MAX_ROWS;

  const patch = (i: number, changes: Partial<Draft>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...changes } : r)));
  /** Array order IS the order (the server reindexes position on save), so a move is a splice. */
  const move = (from: number, to: number) =>
    setRows((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  const remove = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => setRows((prev) => [
    ...prev,
    { linkId: null, source: null, label: "", url: "", icon: "custom", note: "", hidden: false },
  ]);

  const onSave = () => {
    if (invalid || tooMany) return;
    save.mutate(
      {
        // The array order IS the order, across both kinds — the server reindexes position over the whole list.
        // An auto entry sends ONLY what is stored: naming it and whether it is hidden. Echoing its derived
        // label/url would invite the server to freeze a handle that the next sync is about to change.
        entries: rows.map((r) =>
          isManual(r)
            ? {
                linkId: r.linkId ?? undefined,
                label: r.label.trim(),
                url: r.url.trim(),
                icon: r.icon?.trim() || null,
              }
            : { source: r.source, hidden: r.hidden },
        ),
      },
      {
        // Reset the local draft to the server-normalised result (positions reindexed, new rows given ids) — the
        // deliberate post-save reset now that the editor is no longer remounted by a dataUpdatedAt key.
        onSuccess: (saved) => {
          // The baseline is the SERVER-normalised draft (positions reindexed, new rows given ids), not what
          // was submitted — anything else leaves the card reporting unsaved changes it no longer has.
          const next = toDraft(saved);
          setRows(next);
          markSaved(next);
          pushToast("Links saved.", "success");
        },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save your links."), "error"),
      },
    );
  };

  const onCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      pushToast("Public link copied.", "success");
    } catch {
      pushToast("Couldn't copy the link.", "error");
    }
  };

  return (
    // No subtitle (ART-187) — the PO cut the descriptive sub-text from the Assets cards; the URL bar + rows say it.
    <Card id="links" title="Link in Bio" actions={<Button {...saveButtonState(unsaved, save.isPending || invalid || tooMany)} size="xs" onClick={onSave}>
      {save.isPending ? "Saving…" : "Save"}
    </Button>}>


      {publicUrl && (
        <UrlBar url={publicUrl} onCopy={() => void onCopy()} />
      )}

      {rows.length === 0 ? (
        <EmptyState title="No links yet" description="Add tour dates, releases, merch, or socials to build your one-tap funnel." />
      ) : (
        <ol className={styles.rows}>
          {rows.map((r, i) =>
            isManual(r) ? (
              <LinkRow key={r.linkId ?? `new-${i}`} index={i} count={rows.length} row={r} onMove={move}
                onPatch={(changes) => patch(i, changes)} onRemove={() => remove(i)} />
            ) : (
              <AutoRow key={`auto-${r.source}`} index={i} count={rows.length} row={r} onMove={move}
                onToggleHidden={() => patch(i, { hidden: !r.hidden })} />
            ),
          )}
        </ol>
      )}

      <AddRow onClick={add} disabled={tooMany}>
        Add link
      </AddRow>

      {/* The platform auto rows moved UP into the list above (ART-51); this block is now the tour-dates row
          alone, which by the same ruling stays exactly as it was — always last, never reorderable, never
          hideable, because it is an anchor into the page's own #tour-dates section rather than a link out.
          The "Auto-pulled" heading went with them: it was grouping several rows, and over a lone Tour Dates
          row it labelled a category of one. The row's own caption already says where it comes from. */}
      {tour && (
        <div className={styles.autoSection}>
          <ul className={styles.autoList}>
            <li className={styles.autoRow}>
              <TourIcon tour={tour} />
              <div className={styles.autoMain}>
                <span className={styles.autoLabel}>Tour Dates</span>
                <span className={styles.autoNote}>
                  {tour.upcomingCount} upcoming show{tour.upcomingCount === 1 ? "" : "s"} · shown on your page
                </span>
              </div>
            </li>
          </ul>
        </div>
      )}

      {/* Merch (ART-234) — after the tour dates, the page's own order. Self-saving: it does not ride the Save above. */}
      <MerchShelfSection />
    </Card>
  );
}

function absolutePublicUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  const base = PUBLIC_WEB_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}${path}`;
}

function isHttp(url: string): boolean {
  try {
    const u = new URL(url.trim());
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
